import axios from 'axios';

import { CarPartsResponse } from '../responses/PartsResponse';
import { UploadResponse } from '../responses/UploadResponse';

import {
  DeliveryStatusUpdate,
  DeliveryDeadlineUpdate,
  PartsResponse,
  UpdatePartStatusDto,
  PartLogsFilters,
  PartLogsResponse,
} from '../interfaces/Parts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getCarParts = async (
  carId: string,
  page: number = 1,
  limit: number = 10,
  options?: { onlyBudgeted?: boolean; includeRemoved?: boolean },
): Promise<CarPartsResponse> => {
  try {
    const response = await axios.get<CarPartsResponse>(
      `${BACKEND_URL}/parts/${carId}/parts`,
      {
        params: { page, limit, ...options },
        withCredentials: true,
        headers: authHeader(),
      }
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao obter as peças do carro');
    } else {
      throw new Error('Erro desconhecido ao obter as peças do carro');
    }
  }
};

export const fetchParts = async (
  page: number = 1,
  limit: number = 10,
  responsibleName?: string,
): Promise<PartsResponse> => {
  try {
    const response = await axios.get<PartsResponse>(`${BACKEND_URL}/parts`, {
      params: { page, limit, responsibleName },
      withCredentials: true,
      headers: authHeader(),
    });

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao listar peças');
    }
    throw new Error('Erro desconhecido');
  }
};

export const removeBudgetedPart = async (carId: string, partId: string, reason: string) => {
  try {
    // Prefer POST: alguns ambientes/proxies bloqueiam PATCH
    const url = `${BACKEND_URL}/cars/${carId}/parts/${partId}/remove`;
    // Debug rápido para garantir que a URL final não está relativa
    // eslint-disable-next-line no-console
    console.debug('[removeBudgetedPart] url=', url, 'payload=', { reason });

    const response = await axios.post(
      url,
      { reason },
      { withCredentials: true, headers: authHeader() },
    );
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const message = (error.response?.data as any)?.message ?? '';
      const raw = typeof error.response?.data === 'string' ? error.response?.data : '';

      // Fallback 1: tenta PATCH na rota oficial
      if (
        status === 404 ||
        status === 405 ||
        String(message).includes('Cannot POST') ||
        raw.includes('Cannot POST')
      ) {
        try {
          const fallbackUrl = `${BACKEND_URL}/cars/${carId}/parts/${partId}/remove`;
          // eslint-disable-next-line no-console
          console.debug('[removeBudgetedPart] fallback PATCH url=', fallbackUrl, 'payload=', { reason });

          const fallback = await axios.patch(
            fallbackUrl,
            { reason },
            { withCredentials: true, headers: authHeader() },
          );
          return fallback.data;
        } catch (fallbackError: unknown) {
          if (axios.isAxiosError(fallbackError)) {
            const fbStatus = fallbackError.response?.status;
            const fbMessage = (fallbackError.response?.data as any)?.message ?? '';
            const fbRaw = typeof fallbackError.response?.data === 'string' ? fallbackError.response?.data : '';

            // Fallback 2: compat com backends antigos que expõem somente /parts/:carId/parts/:partId/remove
            if (
              fbStatus === 404 ||
              fbStatus === 405 ||
              String(fbMessage).includes('Cannot PATCH') ||
              fbRaw.includes('Cannot PATCH')
            ) {
              try {
                const legacyUrl = `${BACKEND_URL}/parts/${carId}/parts/${partId}/remove`;
                // eslint-disable-next-line no-console
                console.debug('[removeBudgetedPart] legacy POST url=', legacyUrl, 'payload=', { reason });

                const legacy = await axios.post(
                  legacyUrl,
                  { reason },
                  { withCredentials: true, headers: authHeader() },
                );
                return legacy.data;
              } catch (legacyError: unknown) {
                if (axios.isAxiosError(legacyError)) {
                  const legacyStatus = legacyError.response?.status;
                  const legacyMessage = (legacyError.response?.data as any)?.message ?? '';
                  const legacyRaw = typeof legacyError.response?.data === 'string' ? legacyError.response?.data : '';

                  if (
                    legacyStatus === 404 ||
                    legacyStatus === 405 ||
                    String(legacyMessage).includes('Cannot POST') ||
                    legacyRaw.includes('Cannot POST')
                  ) {
                    // Último fallback: PATCH legacy
                    const legacyPatchUrl = `${BACKEND_URL}/parts/${carId}/parts/${partId}/remove`;
                    // eslint-disable-next-line no-console
                    console.debug('[removeBudgetedPart] legacy PATCH url=', legacyPatchUrl, 'payload=', { reason });

                    const legacyPatch = await axios.patch(
                      legacyPatchUrl,
                      { reason },
                      { withCredentials: true, headers: authHeader() },
                    );
                    return legacyPatch.data;
                  }

                  throw new Error((legacyError.response?.data as any)?.message || 'Erro ao remover a peça do orçamento');
                }
                throw new Error('Erro desconhecido');
              }
            }

            throw new Error((fallbackError.response?.data as any)?.message || 'Erro ao remover a peça do orçamento');
          }
          throw new Error('Erro desconhecido');
        }
      }

      throw new Error((error.response?.data as any)?.message || 'Erro ao remover a peça do orçamento');
    }
    throw new Error('Erro desconhecido');
  }
};

export const uploadCarParts = async (
  carId: string,
  file: File,
  isComplement: boolean
): Promise<UploadResponse> => {
  const formData = new FormData();
  formData.append('file', file, file.name);

  try {
    const response = await axios.post(
      `${BACKEND_URL}/upload/xml/${carId}?isComplement=${isComplement}`,
      formData,
      {
        withCredentials: true,
      }
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao fazer upload das peças do carro');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const updateDeliveryStatus = async (statusUpdates: DeliveryStatusUpdate[]) => {
  try {
    const response = await axios.patch(
      `${BACKEND_URL}/parts/delivery-status`,
      statusUpdates,
      {
        withCredentials: true,
      }
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar o status de entrega');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const updateDeliveryDeadline = async (deadlines: DeliveryDeadlineUpdate[]) => {
  try {
    const response = await axios.patch(
      `${BACKEND_URL}/parts/delivery-deadlines`,
      deadlines,
      {
        withCredentials: true,
      }
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar os prazos de entrega');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const searchParts = async (
  name?: string,
  deliveryStatus?: string,
  shopStatus?: string,
  page?: number,
  limit?: number,
  carId?: string,
  responsibleName?: string,
): Promise<PartsResponse> => {
  try {
    const params: Record<string, any> = { name, deliveryStatus };

    if (shopStatus) {
      params.shopStatus = shopStatus;
    }

    if (page && limit) {
      params.page = page;
      params.limit = limit;
    }

    if (carId) {
      params.carId = carId;
    }

    if (responsibleName) {
      params.responsibleName = responsibleName;
    }

    // at-69 (BUG-V85): era a única chamada deste arquivo sem `authHeader()`. O backend lê o
    // cookie antes do header, então onde há cookie a falta passa despercebida — mas o
    // `authToken` do localStorage é a fonte usada por todo o resto do frontend, e sem ele
    // a rota responde 401. Na tela de Logs de Peças isso esvaziava o seletor de peças, que
    // é o passo obrigatório para ver qualquer histórico.
    const response = await axios.get<PartsResponse>(`${BACKEND_URL}/parts/search`, {
      params,
      withCredentials: true,
      headers: authHeader(),
    });

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar as peças');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const updateMultiplePartStatuses = async (parts: UpdatePartStatusDto[]) => {
  try {
    const response = await axios.patch(
      `${BACKEND_URL}/parts/update-status`,
      { parts },
      {
        withCredentials: true,
      }
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao atualizar o status das peças');
    } else {
      throw new Error('Erro desconhecido');
    }
  }
};

export const createManualPart = async (
  carId: string,
  name: string,
  complementaryQuoteNum: number = 0,
  deliveryDeadline?: string,
) => {
  try {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/parts`,
      { carId, name, complementaryQuoteNum, deliveryDeadline },
      { withCredentials: true, headers: authHeader() },
    );
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao cadastrar peça manualmente');
    }
    throw new Error('Erro desconhecido');
  }
};

export const updateManualPart = async (partId: string, name: string) => {
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_BACKEND_URL}/parts/${partId}`,
      { name },
      { withCredentials: true, headers: authHeader() },
    );
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao editar peça');
    }
    throw new Error('Erro desconhecido');
  }
};

export const deleteManualPart = async (partId: string) => {
  try {
    const response = await axios.delete(
      `${process.env.REACT_APP_BACKEND_URL}/parts/${partId}`,
      { withCredentials: true, headers: authHeader() },
    );
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao excluir peça');
    }
    throw new Error('Erro desconhecido');
  }
};

export const fetchPartLogs = async (
  partId: string,
  filters?: PartLogsFilters,
): Promise<PartLogsResponse> => {
  try {
    const response = await axios.get<PartLogsResponse>(
      `${BACKEND_URL}/parts/${partId}/logs`,
      {
        params: filters,
        withCredentials: true,
        headers: authHeader(),
      },
    );

    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      throw new Error(error.response?.data?.message || 'Erro ao buscar logs da peça');
    }

    throw new Error('Erro desconhecido');
  }
};
